import { useTranslation } from '@/i18n';
import { toCommaSeparated } from '@/utils/convert/toCommaSeparated';
import {
  AppliedInferenceField,
  AppliedInferenceSourceDetail,
  InferenceDetailSource,
  InferenceDetailSourceDetail,
  type ApReportInference,
  type AppliedInference,
  type InferenceDetail,
  type InferenceDetailSourceDetail as InferenceDetailSourceDetailType,
  type InferenceDetailSource as InferenceDetailSourceType,
} from 'ap-openapi';
import { DATE_FORMAT, format } from 'date-util';
import dayjs, { type Dayjs } from 'dayjs';

export type { ApReportInference, AppliedInference };

export type InferenceEntry = {
  source: NonNullable<InferenceDetailSourceType>;
  sourceDetail?: NonNullable<InferenceDetailSourceDetailType>;
  value: string;
};

export type ApReportInferencesMap = {
  apPayee: InferenceEntry | undefined;
  payeeBankName: InferenceEntry | undefined;
  payeeBankBranch: InferenceEntry | undefined;
  invoiceRegistrationNumber: InferenceEntry | undefined;
  dueDate: InferenceEntry | undefined;
  totalAmount: InferenceEntry | undefined;
};

export const SOURCE_LABEL_KEY: Record<
  NonNullable<InferenceDetailSourceType>,
  string
> = {
  [InferenceDetailSource.ocr]: 'report_inference_comparison_ocr_label',
  [InferenceDetailSource.ai]: 'report_inference_comparison_ai_label',
  [InferenceDetailSource.user]: 'report_inference_comparison_user_label',
  [InferenceDetailSource.bpo_kihyo]:
    'report_inference_comparison_bpo_kihyo_label',
  // Provisional: The wording for this key is set to "OCR" in ja/en.json.
  // Labels for the remaining source_detail values (counterparty / received_invoice /
  // report) are still to be defined (APWARRIOR-3658), so they fall back to this key.
  [InferenceDetailSource.system]: 'report_inference_comparison_system_label',
  [InferenceDetailSource.peppol]: 'report_inference_comparison_peppol_label',
};

export const SOURCE_DETAIL_LABEL_KEY: Partial<
  Record<NonNullable<InferenceDetailSourceDetailType>, string>
> = {
  [InferenceDetailSourceDetail.payee]:
    'report_inference_comparison_system_payee_label',
};

export const toSourceLabelKey = (
  source: InferenceEntry['source'],
  sourceDetail: InferenceEntry['sourceDetail']
): string => {
  const detailLabelKey =
    source === InferenceDetailSource.system && sourceDetail
      ? SOURCE_DETAIL_LABEL_KEY[sourceDetail]
      : undefined;

  return detailLabelKey ?? SOURCE_LABEL_KEY[source];
};

/**
 * Label separation by each source_detail is within the scope of phase 2.
 * This is a FE-side guard to maintain phase 1 wording even if source_detail is returned while the flag is off, although BE is expected to start returning the same flag.
 */
export const toSourceOnlyEntry = (
  entry: InferenceEntry | undefined
): InferenceEntry | undefined => entry && { ...entry, sourceDetail: undefined };

export const toAppliedDetail = (
  detail: InferenceDetail | undefined,
  applied: {
    value: string;
    source: NonNullable<InferenceDetailSourceType>;
    sourceDetail: NonNullable<InferenceDetailSourceDetailType>;
  }
): InferenceDetail => ({
  ...detail,
  reason: detail?.reason ?? null,
  value: applied.value,
  source: applied.source,
  source_detail: applied.sourceDetail,
});

const toInferenceEntry = (
  detail: InferenceDetail | undefined
): InferenceEntry | undefined => {
  if (!detail?.value || !detail.source) return undefined;
  return {
    source: detail.source,
    sourceDetail: detail.source_detail ?? undefined,
    value: detail.value,
  };
};

const toTrimmedTextInferenceEntry = (
  detail: InferenceDetail | undefined
): InferenceEntry | undefined => {
  const entry = toInferenceEntry(detail);
  if (!entry) return undefined;

  const normalizedValue = entry.value.trim();
  if (normalizedValue === '') return undefined;

  return { ...entry, value: normalizedValue };
};

/**
 * 取引先は `account_number` の推論値（口座番号）で突き合わせる。
 * 口座番号として比較できない値（空白のみ / 非数値）はミスマッチ判定しない。
 */
export const toPayeeInferenceEntry = (
  detail: InferenceDetail | undefined
): InferenceEntry | undefined => {
  const entry = toTrimmedTextInferenceEntry(detail);
  if (!entry || Number.isNaN(Number(entry.value))) return undefined;
  return entry;
};

export const getInferredApPayeeId = (
  inferences: ApReportInference[]
): string | undefined => inferences[0]?.ap_payee?.value ?? undefined;

/**
 * to check if the inference.ap_payee's registered account number matches the OCR-read account number (inference.account_number.value).
 */
export const isInferredPayeeAccountNumberVerified = (
  registeredAccountNumber: string | null | undefined,
  inferredAccountNumber: string | null | undefined
): boolean => {
  if (!registeredAccountNumber || !inferredAccountNumber) return false;

  return registeredAccountNumber === inferredAccountNumber;
};

export const toApReportInferencesMap = (
  inferences: ApReportInference[]
): ApReportInferencesMap => {
  const latestInference = inferences[0];
  return {
    apPayee: toPayeeInferenceEntry(latestInference?.account_number),
    payeeBankName: toTrimmedTextInferenceEntry(latestInference?.bank_name),
    payeeBankBranch: toTrimmedTextInferenceEntry(
      latestInference?.bank_branch_name
    ),
    invoiceRegistrationNumber: toInferenceEntry(
      latestInference?.invoice_registration_number
    ),
    dueDate: toInferenceEntry(latestInference?.due_date),
    totalAmount: toInferenceEntry(latestInference?.total_amount),
  };
};

export const toAppliedInferences = (
  baselineOverrides: Partial<ApReportInference>
): AppliedInference[] => {
  const dueDate: InferenceDetail | undefined = baselineOverrides.due_date;
  if (!dueDate?.value) return [];
  if (dueDate.source_detail !== AppliedInferenceSourceDetail.payee) return [];

  return [
    {
      field: AppliedInferenceField.due_date,
      value: dueDate.value,
      source_detail: dueDate.source_detail,
    },
  ];
};

// toComparable functions; will finalize and update the specifications after the actual data (actual formats of form values, drawer values, and inference values) becomes available.
export const toComparableDueDate = (
  v: string | Dayjs | null | undefined
): string | null => {
  if (v === null || v === undefined) return null;
  const d = dayjs(v);
  return d.isValid()
    ? d.format('YYYY-MM-DD')
    : typeof v === 'string'
    ? v
    : null;
};

export const toComparableTotalAmount = (
  v: string | null | undefined
): string | null => {
  if (v === null || v === undefined) return null;
  const numeric = v.replace(/[^\d.-]/g, '');
  if (numeric === '') return v;

  const n = Number(numeric);
  return Number.isNaN(n) ? v : String(n);
};

// Normalizes the inference value the same way as the current value, to avoid
// false positives from notation differences alone (e.g. "1000.0" vs "1000").
export const toNormalizedTotalAmountInference = (
  inference: InferenceEntry | undefined
): InferenceEntry | undefined =>
  inference && {
    ...inference,
    value: toComparableTotalAmount(inference.value) ?? inference.value,
  };

export type MismatchValueKind = 'amount' | 'date' | 'text';

const toMismatchDisplayValue = (
  value: string,
  kind: MismatchValueKind
): string => {
  switch (kind) {
    case 'amount':
      return toCommaSeparated(value);
    case 'date':
      return format(value, DATE_FORMAT) || value;
    case 'text':
      return value;
  }
};

export const toMismatchMessage = (
  mismatch: InferenceEntry | null | undefined,
  t: ReturnType<typeof useTranslation>['t'],
  kind: MismatchValueKind
): string | undefined => {
  if (!mismatch) return undefined;

  const messageKey =
    kind === 'amount'
      ? 'report_inference_comparison_mismatch_amount_message'
      : 'report_inference_comparison_mismatch_message';

  return t(messageKey, {
    label: t(toSourceLabelKey(mismatch.source, mismatch.sourceDetail)),
    value: toMismatchDisplayValue(mismatch.value, kind),
  });
};

export const getMismatch = (
  inference: InferenceEntry | undefined,
  comparableCurrentValue: string | null | undefined
): InferenceEntry | null => {
  if (!inference) return null;

  if (inference.value === comparableCurrentValue) return null;

  return {
    source: inference.source,
    sourceDetail: inference.sourceDetail,
    value: inference.value,
  };
};

export type PayeeBankInferences = {
  bankName?: InferenceEntry;
  bankBranch?: InferenceEntry;
  accountNumber?: InferenceEntry;
};

export type PayeeBankCurrentValues = {
  accountNumber?: string | null;
  bankName?: string | null;
  bankBranch?: string | null;
};

const collectPayeeBankMismatchValue = (
  inference: InferenceEntry | undefined,
  currentValue: string | null | undefined,
  mismatchedValues: string[]
): Pick<InferenceEntry, 'source' | 'sourceDetail'> | undefined => {
  if (!inference) return undefined;
  // Strict equality: empty current (undefined / null / '') does not equal an
  // OCR value, so an alert is still shown when inference has a value.
  if (inference.value === currentValue) return undefined;

  mismatchedValues.push(inference.value);
  return {
    source: inference.source,
    sourceDetail: inference.sourceDetail,
  };
};

/**
 * Compare OCR payee bank name, bank branch, and account number with the
 * entered values. The mismatch message value is the differing OCR values
 * joined in that order. Even when office/current has no bank fields (e.g.
 * cash payment methods), OCR values still produce a mismatch so the user can
 * review what was detected.
 */
export const getPayeeBankMismatch = (
  inferences: PayeeBankInferences,
  current: PayeeBankCurrentValues
): InferenceEntry | null => {
  const mismatchedValues: string[] = [];

  const bankNameSource = collectPayeeBankMismatchValue(
    inferences.bankName,
    current.bankName,
    mismatchedValues
  );
  const bankBranchSource = collectPayeeBankMismatchValue(
    inferences.bankBranch,
    current.bankBranch,
    mismatchedValues
  );
  const accountNumberSource = collectPayeeBankMismatchValue(
    inferences.accountNumber,
    current.accountNumber,
    mismatchedValues
  );

  const selected = bankNameSource ?? bankBranchSource ?? accountNumberSource;
  if (!selected || mismatchedValues.length === 0) return null;

  return { ...selected, value: mismatchedValues.join(' ') };
};
